import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  ExitToApp,
  Help as HelpIcon,
  PersonAdd
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import ChampionsNavigation from './ChampionsNavigation';
import SupportDialog from '../SupportDialog';
import InviteDialog from '../InviteDialog';
import {
  handleProfile,
  handleChangePassword,
  handleLogout
} from '../common/GeneralnNagivationHandler';

const ChampionsLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);



  // Close mobile drawer when window is resized to desktop
  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleProfileClick = () => {
    handleProfile(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleChangePasswordClick = () => {
    handleChangePassword(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleLogout(logout, setMobileOpen, navigate);
    setAnchorEl(null);
  };

  const handleSupportClick = () => {
    setSupportOpen(true);
  };

  const handleInviteClick = () => {
    setInviteOpen(true);
  };

  // Get current page title
  const getPageTitle = () => {
    const pathMap = {
      '/champions/dashboard': 'Champions Dashboard',
      '/champions/referral': 'Champions Referral',
      '/champions/questionbank': 'Champions Question Bank',
      '/profile': 'My Profile',
      '/settings/change-password': 'Change Password'
    };
    return pathMap[location.pathname] || 'Champions Dashboard';
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Navigation Drawer */}
      <ChampionsNavigation
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        supportOpen={supportOpen}
        onSupportOpen={setSupportOpen}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { 
            sm: `calc(100% - ${collapsed ? 72 : 280}px)` 
          },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { 
              sm: `calc(100% - ${collapsed ? 72 : 280}px)` 
            },
            ml: { sm: `${collapsed ? 72 : 280}px` },
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            
            <Box sx={{ flexGrow: 1 }}>
              {/* Top bar text removed */}
            </Box>

            {/* User Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Invite Button - Only show for champions */}
              {user && user.role_name?.toUpperCase() === 'CHAMPIONS' && (
                <Tooltip title="Invite others and get rewarded">
                  <IconButton
                    onClick={handleInviteClick}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.50',
                      }
                    }}
                  >
                    <PersonAdd />
                  </IconButton>
                </Tooltip>
              )}
              
              {/* Need Help Button */}
              <Tooltip title="Reach out for help">
                <IconButton
                  onClick={handleSupportClick}
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.50',
                    }
                  }}
                >
                  <HelpIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Account settings">
                <IconButton
                  size="large"
                  edge="end"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  color="inherit"
                  sx={{ mr: 1 }}
                  aria-label="User account menu"
                >
                  <Avatar 
                    sx={{ 
                      width: 36, 
                      height: 36, 
                      bgcolor: 'primary.main',
                      fontSize: '0.875rem'
                    }}
                    src={user?.profile_picture}
                  >
                    {user?.first_name?.charAt(0) || (user?.role_name?.toUpperCase() === 'CHAMPIONS' ? 'C' : 'U')}
                  </Avatar>
                </IconButton>
              </Tooltip>
              
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                onClick={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    minWidth: 200,
                    mt: 1
                  }
                }}
              >
                <MenuItem disabled>
                  <ListItemText 
                    primary={user?.email || (user?.role_name?.toUpperCase() === 'CHAMPIONS' ? 'champion@champions.com' : 'user@example.com')}
                    primaryTypographyProps={{ 
                      fontSize: '0.75rem',
                      color: 'text.secondary'
                    }}
                  />
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleChangePasswordClick}>
                  <ListItemIcon>
                    <VpnKeyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Change Password</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogoutClick}>
                  <ListItemIcon>
                    <ExitToApp fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Full-width separator line */}
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 56, sm: 64 }, // Match AppBar height
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'divider',
            zIndex: theme.zIndex.appBar - 1,
          }}
        />

        {/* Main Content */}
        <Box
          sx={{
            pt: { xs: 7, sm: 8 },
            px: { xs: 2, sm: 3 },
            py: 0,
            minHeight: '100vh',
            backgroundColor: 'grey.50',
            ml: { sm: `${collapsed ? 72 : 280}px` },
            mt: 1, // Add small top margin for better spacing
            transition: theme.transitions.create('margin-left', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Support Dialog */}
      <SupportDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        presetEmail={user?.email || ''}
      />

      {/* Invite Dialog */}
      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </Box>
  );
};

export default ChampionsLayout;
